import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export function useUserFollows() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const { data: followingUsers = [], isLoading } = useQuery({
    queryKey: ['user-follows', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', profile.id);
      
      if (error) throw error;
      return data.map(f => f.following_id);
    },
    enabled: !!profile?.id,
  });

  const followUser = useMutation({
    mutationFn: async (followingId: string) => {
      if (!profile?.id) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('user_follows')
        .insert({ follower_id: profile.id, following_id: followingId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-follows'] });
      toast.success('Đã theo dõi');
    },
    onError: () => {
      toast.error('Không thể theo dõi');
    },
  });

  const unfollowUser = useMutation({
    mutationFn: async (followingId: string) => {
      if (!profile?.id) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', profile.id)
        .eq('following_id', followingId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-follows'] });
      toast.success('Đã bỏ theo dõi');
    },
    onError: () => {
      toast.error('Không thể bỏ theo dõi');
    },
  });

  const isFollowingUser = (userId: string) => followingUsers.includes(userId);

  return {
    followingUsers,
    isLoading,
    followUser,
    unfollowUser,
    isFollowingUser,
  };
}

export function useClubFollows() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const { data: followingClubs = [], isLoading } = useQuery({
    queryKey: ['club-follows', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase
        .from('club_follows')
        .select('club_id')
        .eq('profile_id', profile.id);
      
      if (error) throw error;
      return data.map(f => f.club_id);
    },
    enabled: !!profile?.id,
  });

  const followClub = useMutation({
    mutationFn: async (clubId: string) => {
      if (!profile?.id) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('club_follows')
        .insert({ profile_id: profile.id, club_id: clubId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-follows'] });
      toast.success('Đã theo dõi CLB');
    },
    onError: () => {
      toast.error('Không thể theo dõi CLB');
    },
  });

  const unfollowClub = useMutation({
    mutationFn: async (clubId: string) => {
      if (!profile?.id) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('club_follows')
        .delete()
        .eq('profile_id', profile.id)
        .eq('club_id', clubId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-follows'] });
      toast.success('Đã bỏ theo dõi CLB');
    },
    onError: () => {
      toast.error('Không thể bỏ theo dõi CLB');
    },
  });

  const isFollowingClub = (clubId: string) => followingClubs.includes(clubId);

  return {
    followingClubs,
    isLoading,
    followClub,
    unfollowClub,
    isFollowingClub,
  };
}

export function useFollowCounts(profileId?: string) {
  return useQuery({
    queryKey: ['follow-counts', profileId],
    queryFn: async () => {
      if (!profileId) return { followers: 0, following: 0 };
      
      const [followersRes, followingRes] = await Promise.all([
        supabase
          .from('user_follows')
          .select('id', { count: 'exact', head: true })
          .eq('following_id', profileId),
        supabase
          .from('user_follows')
          .select('id', { count: 'exact', head: true })
          .eq('follower_id', profileId),
      ]);

      return {
        followers: followersRes.count || 0,
        following: followingRes.count || 0,
      };
    },
    enabled: !!profileId,
  });
}

export function useClubFollowersCount(clubId?: string) {
  return useQuery({
    queryKey: ['club-followers-count', clubId],
    queryFn: async () => {
      if (!clubId) return 0;
      
      const { count, error } = await supabase
        .from('club_follows')
        .select('id', { count: 'exact', head: true })
        .eq('club_id', clubId);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!clubId,
  });
}
